import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import { labelStatus } from './langganan/langgananUtil';

const ROLE_LABEL = { kasir: 'Kasir', karyawan: 'Karyawan', dapur: 'Dapur', admin: 'Admin' };

const ringkasAkun = (akun) => {
  const hitung = {};
  akun.forEach((u) => { hitung[u.role] = (hitung[u.role] || 0) + 1; });
  const teks = Object.entries(hitung).map(([r, n]) => `${n} ${ROLE_LABEL[r] || r}`);
  return teks.length ? teks.join(', ') : 'Belum ada';
};

// Tampilan superadmin: daftar cabang beserta admin cabangnya; klik baris untuk melihat akun role lain.
const UserCabangGroups = ({ users, cabangList, searchTerm, filterRole, onEdit, onDelete, onAdd }) => {
  const [terbuka, setTerbuka] = useState({});
  const cari = searchTerm.trim().toLowerCase();

  const grup = useMemo(() => {
    const perCabang = new Map(cabangList.map((c) => [c.uuid, { cabang: c, admin: [], lain: [] }]));
    const tanpaCabang = [];
    users.forEach((u) => {
      const g = u.cabanguuid ? perCabang.get(u.cabanguuid) : null;
      if (!g) {
        tanpaCabang.push(u); // mis. superadmin: sengaja tidak ditampilkan agar tidak terhapus/terubah
      } else if (u.role === 'admin') {
        g.admin.push(u);
      } else {
        g.lain.push(u);
      }
    });
    return { daftar: [...perCabang.values()], tanpaCabang };
  }, [users, cabangList]);

  const cocokUser = (u) => (!cari || u.username.toLowerCase().includes(cari) || (u.nama || '').toLowerCase().includes(cari))
    && (!filterRole || u.role === filterRole);

  const tampil = grup.daftar.filter((g) => {
    const namaCocok = cari && g.cabang.namacabang.toLowerCase().includes(cari);
    if (!cari && !filterRole) return true;
    if (namaCocok && !filterRole) return true;
    return [...g.admin, ...g.lain].some(cocokUser);
  });

  const toggle = (uuid) => setTerbuka((p) => ({ ...p, [uuid]: !p[uuid] }));

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width={48} />
              <TableCell>Cabang</TableCell>
              <TableCell>Admin Cabang</TableCell>
              <TableCell>Akun Lain</TableCell>
              <TableCell>Langganan</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tampil.map(({ cabang, admin, lain }) => {
              const lainTampil = lain.filter(cocokUser);
              const buka = Boolean(terbuka[cabang.uuid]) || Boolean(cari || filterRole);
              const status = labelStatus(cabang.langganan);
              return (
                <React.Fragment key={cabang.uuid}>
                  <TableRow hover sx={{ '& > td': { borderBottom: buka ? 'unset' : undefined, verticalAlign: 'top' } }}>
                    <TableCell>
                      <IconButton size="small" onClick={() => toggle(cabang.uuid)} aria-label="lihat akun lain">
                        {buka ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                      </IconButton>
                    </TableCell>
                    <TableCell onClick={() => toggle(cabang.uuid)} sx={{ cursor: 'pointer' }}>
                      <Typography fontWeight={700}>{cabang.namacabang}</Typography>
                      <Typography variant="caption" color="text.secondary">{cabang.tipe_cabang}</Typography>
                    </TableCell>
                    <TableCell>
                      {admin.length === 0 && (
                        <Box>
                          <Typography variant="body2" color="text.secondary">Belum ada admin</Typography>
                          <Button size="small" onClick={() => onAdd({ cabanguuid: cabang.uuid, role: 'admin' })}>Tambah Admin</Button>
                        </Box>
                      )}
                      {admin.map((a) => (
                        <Box key={a.uuid} sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                          <Box sx={{ minWidth: 150 }}>
                            <Typography variant="body2" fontWeight={600}>{a.username}</Typography>
                            <Typography variant="caption" color="text.secondary">{a.nama || '-'}</Typography>
                          </Box>
                          <Button size="small" onClick={() => onEdit(a)}>Edit</Button>
                          <Button size="small" color="error" onClick={() => onDelete(a.uuid)}>Delete</Button>
                        </Box>
                      ))}
                    </TableCell>
                    <TableCell onClick={() => toggle(cabang.uuid)} sx={{ cursor: 'pointer' }}>{ringkasAkun(lain)}</TableCell>
                    <TableCell>
                      <Chip size="small" color={status.color} label={status.text} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={5} sx={{ py: 0, borderBottom: buka ? undefined : 'unset' }}>
                      <Collapse in={buka} timeout="auto" unmountOnExit>
                        <Box sx={{ m: 1, ml: 6 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2">Akun di {cabang.namacabang}</Typography>
                            <Button size="small" variant="outlined" onClick={() => onAdd({ cabanguuid: cabang.uuid, role: '' })}>
                              Tambah Akun
                            </Button>
                          </Box>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Username</TableCell>
                                <TableCell>Nama</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell>Aksi</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {lainTampil.map((u) => (
                                <TableRow key={u.uuid}>
                                  <TableCell>{u.username}</TableCell>
                                  <TableCell>{u.nama || '-'}</TableCell>
                                  <TableCell>{ROLE_LABEL[u.role] || u.role}</TableCell>
                                  <TableCell>
                                    <Button size="small" onClick={() => onEdit(u)}>Edit</Button>
                                    <Button size="small" color="error" onClick={() => onDelete(u.uuid)}>Delete</Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                              {lainTampil.length === 0 && (
                                <TableRow>
                                  <TableCell colSpan={4} align="center">Belum ada akun kasir, karyawan, atau dapur di cabang ini.</TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
            {tampil.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">Tidak ada cabang atau user yang cocok.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

    </>
  );
};

export default UserCabangGroups;
